import Accordion from 'LuxKit/Accordion'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React from 'react'

export default function TravelApprovals() {
  return (
    <Group direction="vertical" gap={8}>
      <Heading variant="heading4">Travel approvals</Heading>
      <Accordion size="large" heading="Why do I need an approval process?">
        <BodyText variant="large">
          Implementing an approval process for corporate travel may contribute to the overall efficiency, compliance and control of travel management within an organisation. Here are some key reasons why an approval process is necessary:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Cost Control:</BodyText>
              <BodyText variant="large"> An approval process allows for oversight of travel expenses, ensuring that proposed trips align with budgetary constraints and financial objectives. Approvers can evaluate the necessity of travel, assess alternative options and make informed decisions to optimise spending.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Policy Adherence:</BodyText>
              <BodyText variant="large"> By routing travel requests through approvers, organisations can enforce compliance with travel policies and guidelines. Approvers can verify that proposed trips meet established criteria, reducing the risk of non-compliant bookings.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I set up an approval process?">
        <BodyText variant="large">
          With Business Traveller, you can establish an approval process to ensure that travel requests are reviewed and authorised by designated approvers. Here's how to set it up:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the Approval Process Settings:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the 'Approval Process' tab, located beneath 'Settings'.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Access the Approval Process Settings:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the 'Approval Process' tab, located beneath 'Settings'.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Designate Approvers:</BodyText>
              <BodyText variant="large"> Identify individuals who will serve as approvers for travel requests within the designated policy scope.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Activate Approval Process:</BodyText>
              <BodyText variant="large"> Once you're satisfied with the setup, activate the approval process to begin routing travel requests through the designated approvers. Once activated, all travel requests submitted within the specified policy scope will be subject to the configured approval process.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          If you need further assistance or guidance in setting up your approval process, please reach out to our support team for assistance.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="Can I customise the approval process for different types of trips or travellers?">
        <BodyText variant="large">
          Business Traveller currently only supports one approval process in place at a time, which applies universally to all travel requests within your organisation.
        </BodyText>
        <br />
        <BodyText variant="large">
          If you have specific requirements or preferences for customisation, please let our Business Traveller team know and they may take this on board for future product enhancements.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="What happens when an approval request is denied?">
        <BodyText variant="large">
          The next steps for an approval request being denied depends on if your company has a travel policy in place.
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Travel Policy in Place:</BodyText>
              <ul>
                <li>
                  <BodyText variant="large">
                    A Traveller will receive a rejection notification if their approval request is denied for being in conflict of your company's travel policy (ie exceeding budget or violating policy guidelines).
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="large">
                    Upon receiving the notification, the Traveller is required to re-select flights or accommodation that align with your company's budget and policy constraints.
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="large">
                    The Traveller can also make adjustments to their booking within the parameters of the travel policy and resubmit the request for approval.
                  </BodyText>
                </li>
              </ul>
            </li>
            <li>
              <BodyText variant="large" weight="bold">No Travel Policy in Place:</BodyText>
              <ul>
                <li>
                  <BodyText variant="large">
                    The Traveller will be promptly notified of their request denial if a mandatory approval process in effect, even in the absence of a travel policy.
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="large">
                    The Traveller will be prompted to re-select their flights or accommodation that meet the requirements for another round of approval.
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="large">
                    At this stage, the Traveller may choose to reach out to the designated approvers outside of the Business Traveller platform to seek clarification or confirmation of their request.
                  </BodyText>
                </li>
              </ul>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          In both scenarios, the denial of an approval request initiates a process for the Traveller to revise their booking to align with company policies, budget constraints or any specific approval criteria. This ensures compliance with organisational guidelines and facilitates efficient travel management within your company.
        </BodyText>
      </Accordion>
    </Group>
  )
}
